/**
 * xlsx merge engine (B1). Injects deal values into the REAL BRITZMEDI form via
 * surgical ZIP/XML editing — NOT exceljs (which cannot parse these forms'
 * drawing anchors). Only worksheet cell values change; xl/media/* (logo +
 * signature/seal image), xl/drawings/*, xl/styles.xml stay byte-for-byte intact,
 * so the output is pixel-identical to the original form (OD-1).
 *
 * Pure module: browser + Node safe. NO `fs`/`path` — the blank template bytes
 * are embedded (template.ts → template-data.ts).
 *
 * Output: an xlsx as ArrayBuffer (browser: Blob/download; Node: Buffer.from).
 */

import type { RuleContext, ShipperProfile } from "@/types";
import { blankTemplateBytes, DISCLAIMER_KO } from "./template";
import {
  CI_PL_MERGE_MAP,
  type HeaderMap,
  type TableSpec,
} from "./merge-map";
import {
  loadXlsx,
  readPart,
  writePart,
  toXlsxBytes,
  patchSheetXml,
  dateToSerial,
  type CellEdit,
} from "./xlsx-zip";

export interface MergeOptions {
  /** Override invoice number (default: "CI-{deal_no}"). */
  invoiceNo?: string;
  /** Issue date string (default: today, YYYY-MM-DD). */
  date?: string;
  /**
   * Shipper/exporter profile to render in the Shipper block + beneficiary bank.
   * If omitted the merge falls back to the deal's shipper data on the context
   * (none today) and renders an empty block — pass it from the UI/demo seed.
   */
  shipper?: ShipperProfile | null;
  /**
   * v2 — override the invoice sheet title (cell A1), e.g. "PROFORMA  INVOICE".
   * Omitted = template title untouched (CI behaviour unchanged).
   */
  docTitle?: string;
}

/** Flattened model the merge map header paths resolve against. */
interface MergeModel {
  shipper: {
    company_name: string;
    address: string;
    tel: string;
    fax: string;
    attn: string;
    bank_name_line: string;
    bank_account_line: string;
    bank_swift_line: string;
  };
  consignee: {
    company_name: string;
    address: string;
    address2: string;
    tax_id: string;
    attn: string;
  };
  doc: {
    invoice_no: string;
    date: string;
    final_destination: string;
    incoterms_remark: string;
  };
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Recompute line_amount deterministically (DP / FR-18: always recompute). */
function lineAmount(qty: number, unitPrice: number): number {
  return Math.round(qty * unitPrice * 100) / 100;
}

function str(v: unknown): string {
  return v == null ? "" : String(v);
}

function buildModel(ctx: RuleContext, opts: MergeOptions): MergeModel {
  const consignee = ctx.partners.consignee ?? ctx.partners.buyer ?? null;
  const shipper = opts.shipper ?? null;

  const incotermRemark = ctx.deal.incoterm_id
    ? `INCOTERMS : ${ctx.deal.incoterm_id}${
        ctx.deal.incoterm_named_place
          ? ` ${ctx.deal.incoterm_named_place}`
          : ""
      } (Incoterms® ${ctx.deal.incoterm_version})`
    : "";

  const bank = shipper?.beneficiary_bank ?? null;

  // The form's consignee address is two physical lines; we split the partner
  // address on the first comma so line 1 = street and line 2 = city/region.
  const consigneeAddr = str(consignee?.address);
  const commaIdx = consigneeAddr.indexOf(",");
  const addr1 =
    commaIdx > 0 ? consigneeAddr.slice(0, commaIdx + 1) : consigneeAddr;
  const addr2 = commaIdx > 0 ? consigneeAddr.slice(commaIdx + 1).trim() : "";

  return {
    shipper: {
      company_name: str(shipper?.company_name) || "BRITZMEDI Co., Ltd.",
      address: str(shipper?.address),
      tel: "",
      fax: "",
      attn: "",
      bank_name_line: bank ? ` Bank Name: ${bank.bank_name}` : "",
      bank_account_line: bank ? ` Account Number: ${bank.account_no}` : "",
      bank_swift_line: bank ? `    SWIFT CODE:  ${bank.swift}` : "",
    },
    consignee: {
      company_name: str(consignee?.company_name),
      address: addr1,
      address2: addr2,
      tax_id: str(consignee?.tax_vat_id),
      attn: consignee?.contact_person ? `ATTN : ${consignee.contact_person}` : "",
    },
    doc: {
      invoice_no: opts.invoiceNo ?? `CI-${ctx.deal.deal_no}`,
      date: opts.date ?? today(),
      final_destination:
        str(ctx.country.name_en) ||
        str(ctx.deal.destination_country),
      incoterms_remark: incotermRemark,
    },
  };
}

/** Resolve a dotted path like "doc.invoice_no" against the merge model. */
function resolvePath(model: MergeModel, path: string): unknown {
  return path
    .split(".")
    .reduce<unknown>(
      (acc, key) =>
        acc && typeof acc === "object"
          ? (acc as Record<string, unknown>)[key]
          : undefined,
      model,
    );
}

/** Build cell edits for a sheet's scalar header (text + date serials). */
function headerEdits(map: HeaderMap, model: MergeModel): CellEdit[] {
  const edits: CellEdit[] = [];
  for (const [ref, path] of Object.entries(map.text)) {
    edits.push({ ref, kind: "string", value: str(resolvePath(model, path)) });
  }
  for (const [ref, path] of Object.entries(map.date)) {
    const v = str(resolvePath(model, path));
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
      edits.push({ ref, kind: "number", value: dateToSerial(v) });
    } else if (v) {
      edits.push({ ref, kind: "string", value: v });
    }
  }
  return edits;
}

/** Resolve a packing measurement string for the CBM column. */
function cbmText(cbm: number | null | undefined): string {
  return cbm == null ? "" : `${cbm} CBM`;
}

/**
 * Build cell edits for a repeating line-item table. Recomputes line_amount on
 * the invoice. Returns { edits, count }. Lines beyond `end_row` are dropped
 * (form has a fixed line area); count reflects rendered lines.
 */
function tableEdits(
  spec: TableSpec,
  ctx: RuleContext,
): { edits: CellEdit[]; count: number; totalQty: number; totalAmount: number; totalNet: number; totalGross: number } {
  const lines = ctx.lines.filter((l) => l.item_kind === "product");
  const edits: CellEdit[] = [];
  const numeric = new Set(spec.numeric_cols);
  let totalQty = 0;
  let totalAmount = 0;
  let totalNet = 0;
  let totalGross = 0;

  const capacity = spec.end_row - spec.start_row + 1;
  const rendered = lines.slice(0, capacity);

  rendered.forEach((line, i) => {
    const rowNo = spec.start_row + i;
    for (const [col, field] of Object.entries(spec.cols)) {
      const ref = `${col}${rowNo}`;
      let value: unknown;
      if (field === "line_amount") {
        value = lineAmount(line.quantity, line.unit_price);
      } else if (field === "cbm_text") {
        value = cbmText(line.cbm);
      } else {
        value = (line as unknown as Record<string, unknown>)[field];
      }
      if (numeric.has(col)) {
        const num = typeof value === "number" ? value : Number(value);
        edits.push({ ref, kind: "number", value: Number.isFinite(num) ? num : 0 });
      } else {
        edits.push({ ref, kind: "string", value: str(value) });
      }
    }
    totalQty += line.quantity;
    totalAmount += lineAmount(line.quantity, line.unit_price);
    totalNet += line.net_weight_kg ?? 0;
    totalGross += line.gross_weight_kg ?? 0;
  });

  // Blank any leftover form line rows that the original had data in.
  for (let r = spec.start_row + rendered.length; r <= spec.end_row; r++) {
    for (const col of Object.keys(spec.cols)) {
      edits.push({ ref: `${col}${r}`, kind: "blank" });
    }
  }

  return {
    edits,
    count: rendered.length,
    totalQty,
    totalAmount: Math.round(totalAmount * 100) / 100,
    totalNet: Math.round(totalNet * 1000) / 1000,
    totalGross: Math.round(totalGross * 1000) / 1000,
  };
}

/**
 * Generate the merged CI + PL workbook for a deal context.
 * Returns the xlsx bytes (ArrayBuffer) — caller decides Blob (browser) vs
 * Buffer.from (Node). The original logo + signature/seal image are preserved.
 */
export async function generateInvoicePacking(
  ctx: RuleContext,
  opts: MergeOptions = {},
): Promise<ArrayBuffer> {
  const model = buildModel(ctx, opts);
  const zip = await loadXlsx(blankTemplateBytes());

  // --- INVOICE-US -----------------------------------------------------------
  const inv = CI_PL_MERGE_MAP.invoice;
  let invXml = await readPart(zip, inv.part);
  const invHeader = headerEdits(inv.header, model);
  const invTable = tableEdits(inv.table, ctx);
  const invEdits: CellEdit[] = [
    // v2: optional invoice title override (PI reuses the same form family).
    ...(opts.docTitle
      ? [{ ref: "A1", kind: "string", value: opts.docTitle } as CellEdit]
      : []),
    ...invHeader,
    ...invTable.edits,
    // totals (numeric, literal — drop the SUM formula so non-recalc viewers show it)
    { ref: inv.totals.qty_cell, kind: "number", value: invTable.totalQty },
    { ref: inv.totals.amount_cell, kind: "number", value: invTable.totalAmount },
    // body-grade disclaimer (CONTRACT invariant #5)
    { ref: inv.disclaimer_cell, kind: "string", value: DISCLAIMER_KO },
  ];
  invXml = patchSheetXml(invXml, invEdits);
  writePart(zip, inv.part, invXml);

  // --- PACKING LIST ---------------------------------------------------------
  const pk = CI_PL_MERGE_MAP.packing;
  let pkXml = await readPart(zip, pk.part);
  const pkHeader = headerEdits(pk.header, model);
  const pkTable = tableEdits(pk.table, ctx);
  const pkEdits: CellEdit[] = [
    ...pkHeader,
    ...pkTable.edits,
    { ref: pk.totals.qty_cell, kind: "number", value: pkTable.totalQty },
    { ref: pk.totals.net_weight_cell, kind: "number", value: pkTable.totalNet },
    {
      ref: pk.totals.gross_weight_cell,
      kind: "number",
      value: pkTable.totalGross,
    },
    { ref: pk.disclaimer_cell, kind: "string", value: DISCLAIMER_KO },
  ];
  pkXml = patchSheetXml(pkXml, pkEdits);
  writePart(zip, pk.part, pkXml);

  return toXlsxBytes(zip);
}

/**
 * v2 — Proforma Invoice. Same BRITZMEDI form family (the real Shamrock PI uses
 * the identical INVOICE-US + PACKING LIST layout); only the sheet title and the
 * document-number prefix differ.
 */
export async function generateProformaInvoice(
  ctx: RuleContext,
  opts: MergeOptions = {},
): Promise<ArrayBuffer> {
  return generateInvoicePacking(ctx, {
    ...opts,
    invoiceNo: opts.invoiceNo ?? `PI-${ctx.deal.deal_no}`,
    docTitle: opts.docTitle ?? "PROFORMA  INVOICE",
  });
}
